import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import JoditEditor from 'jodit-react';
import { addVendorBillValidationSchema } from '../../../validations/SocietyPortal/VendorValidation';
import { getAllVendors, createVendorBill, createVendorBillsBatch, resetVendorBillsBatch, getAllVendorTariffs, getVendorBillingSettings } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import * as routes from '../../../Routing/routes';
import { selectedSocietyStorer } from '../../../utils/webStorer';

const BILL_TYPES = [
  { value: 'po', label: 'Purchase Order' },
  { value: 'tax', label: 'Tax Invoice' },
];

const emptyTariffRow = {
  headId: '',
  glCode: '',
  headName: '',
  qty: '',
  unit: '',
  amount: '',
  cgstRate: '',
  sgstRate: '',
  igstRate: '',
  tdsRate: 0,
  tdsSection: '',
  itemType: '',
};

const AddVendorBill = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    allVendorsData,
    allVendorsLoading,
    createVendorBillLoading,
    createVendorBillsBatchLoading,
    createVendorBillsBatchError,
    vendorSettingLoading,
    allVendorTariffsData,
    allVendorTariffsLoading,
    vendorBillingSettingsData
  } = useSelector(state => state.VendorReducers);
  const setFieldValueRef = useRef(null);
  const descriptionEditorRef = useRef(null);
  const submitModeRef = useRef('post');
  const nextQueuedBillId = useRef(1);
  const [queuedBills, setQueuedBills] = useState([]);
  const [batchResult, setBatchResult] = useState(null);

  const editorConfig = useMemo(() => ({
    readonly: false,
    placeholder: 'Bill description',
    height: 200,
    toolbarButtonSize: 'small',
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'brush', '|',
      'align', '|',
      'link', '|',
      'undo', 'redo', '|',
      'eraser', 'source'
    ],
    removeButtons: ['about'],
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
  }), []);

  useEffect(() => {
    dispatch(getAllVendors({ society_id: selectedSocietyStorer.getSocietyId() }));
    dispatch(getVendorBillingSettings({ societyId: selectedSocietyStorer.getSocietyId() }));
  }, [dispatch]);

  const vendorsList = useMemo(() => {
    if (!Array.isArray(allVendorsData)) return [];
    return allVendorsData.map(vendor => ({
      id: vendor.vendor_id,
      name: vendor.business_name,
      rcmApplicable: vendor.rcm_applicable || 'N',
      tdsApplicable: vendor.tds_applicable || 'N',
      panNumber: vendor.pan_number || '',
    }));
  }, [allVendorsData]);

  const vendorTariffsList = useMemo(() => {
    if (!Array.isArray(allVendorTariffsData)) return [];
    const excludedHeads = ['sgst', 'igst', 'cgst', 'roundoff', 'round_off', 'round off', 'rcm_sgst', 'rcm sgst', 'rcm_igst', 'rcm igst', 'rcm_cgst', 'rcm cgst'];
    return allVendorTariffsData
      .filter(tariff => {
        const name = (tariff.item_name || '').toLowerCase().trim();
        return !excludedHeads.includes(name);
      })
      .map(tariff => ({
        id: tariff.vendor_tariff_id || tariff.tariff_id || tariff.id,
        itemName: tariff.item_name,
        // Expense GL account of the item - sent with the line so the journal's
        // debit side points at an account rather than the tariff id.
        glCode: tariff.gl_name || '',
        itemType: tariff.item_type || '',
        unitRate: parseFloat(tariff.unit_rate) || 0,
        unit: tariff.unit || 'nos',
        cgstRate: parseFloat(tariff.cgst_rate) || 0,
        sgstRate: parseFloat(tariff.sgst_rate) || 0,
        igstRate: parseFloat(tariff.igst_rate) || 0,
        tdsRate: parseFloat(tariff.tds_rate) || 0,
        tdsSection: tariff.tds_section || '',
      }));
  }, [allVendorTariffsData]);

  // Get itemType from billing settings (head_type from getVendorBillingSettings e.g. "UNIT" or "AMOUNT")
  const itemType = (vendorBillingSettingsData?.head_type || vendorBillingSettingsData?.item_type || 'amount').toLowerCase();

  const initialValues = {
    vendorId: '',
    billType: '',
    billNumber: '',
    billDate: '',
    description: '',
    dueDate: '',
    tariffDetails: [{ ...emptyTariffRow }],
    rcmApplicable: 'N',
    tdsApplicable: 'N',
    panNumber: '',
    // GL codes from billing settings
    sgstGlCode: '',
    cgstGlCode: '',
    igstGlCode: '',
    rcmSgstCode: '',
    rcmCgstCode: '',
    rcmIgstCode: '',
    rcmCgstReceivableCode: '',
    rcmSgstReceivableCode: '',
    rcmIgstReceivableCode: '',
    tdsGlCode: '',
    roundOffGlCode: '',
    // Calculated amounts
    sgstAmount: 0,
    cgstAmount: 0,
    igstAmount: 0,
    rcmSgstAmount: 0,
    rcmCgstAmount: 0,
    rcmIgstAmount: 0,
    rcmSgstReceivableAmount: 0,
    rcmCgstReceivableAmount: 0,
    rcmIgstReceivableAmount: 0,
    tdsAmount: 0,
    roundOff: 0,
    subTotal: 0,
    netAmount: 0,
  };

  const safeParseFloat = (value, fallback = 0) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  };

  const calcRowTotals = useCallback((row) => {
    const qty = safeParseFloat(row.qty);
    const unitRate = safeParseFloat(row.amount);
    const base = itemType === 'unit' ? qty * unitRate : safeParseFloat(row.amount);
    const cgst = base * safeParseFloat(row.cgstRate) / 100;
    const sgst = base * safeParseFloat(row.sgstRate) / 100;
    const igst = base * safeParseFloat(row.igstRate) / 100;
    const tdsRate = safeParseFloat(row.tdsRate);
    const tds = base * tdsRate / 100;
    return { base, cgst, sgst, igst, tds, rowTotal: base + cgst + sgst + igst };
  }, [itemType]);

  const calcGrandTotals = useCallback((tariffDetails, rcmApplicable = 'N') => {
    let subtotal = 0, totalCgst = 0, totalSgst = 0, totalIgst = 0, totalTds = 0;
    tariffDetails.forEach(row => {
      const t = calcRowTotals(row);
      subtotal += t.base;
      totalCgst += t.cgst;
      totalSgst += t.sgst;
      totalIgst += t.igst;
      totalTds += t.tds;
    });

    let grandTotal = subtotal + totalCgst + totalSgst + totalIgst;

    // If RCM is applicable, GST is not added to the bill total
    if (rcmApplicable === 'Y') {
      grandTotal = subtotal; // exclude GST from total
    }

    const roundOff = Math.round(grandTotal) - grandTotal;
    const subTotal = Math.round(grandTotal);
    totalTds = Math.round(totalTds);
    const netAmount = subTotal - totalTds;

    return {
      subtotal,
      totalCgst,
      totalSgst,
      totalIgst,
      totalTds,
      roundOff,
      subTotal,
      netAmount,
      // RCM amounts (negative receivable)
      rcmSgst: rcmApplicable === 'Y' ? totalSgst : 0,
      rcmCgst: rcmApplicable === 'Y' ? totalCgst : 0,
      rcmIgst: rcmApplicable === 'Y' ? totalIgst : 0,
    };
  }, [calcRowTotals]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(val);
  };

  const buildBillPayload = (values, isDraft) => {
    const totals = calcGrandTotals(values.tariffDetails, values.rcmApplicable);
    return {
      is_draft: isDraft ? 1 : 0,
      societyId: selectedSocietyStorer.getSocietyId(),
      vendorId: values.vendorId,
      billType: values.billType,
      billNumber: values.billNumber,
      billDate: values.billDate,
      dueDate: values.dueDate,
      description: values.description,
      rcmApplicable: values.rcmApplicable,
      tdsApplicable: values.tdsApplicable,
      panNumber: values.panNumber,
      tariffDetails: values.tariffDetails.map(row => ({
        headId: row.headId,
        headName: row.headName,
        glCode: row.glCode || '',
        itemType: row.itemType,
        qty: safeParseFloat(row.qty),
        unit: row.unit,
        amount: safeParseFloat(row.amount),
        cgstRate: safeParseFloat(row.cgstRate),
        sgstRate: safeParseFloat(row.sgstRate),
        igstRate: safeParseFloat(row.igstRate),
        tdsRate: safeParseFloat(row.tdsRate),
        tdsSection: row.tdsSection,
      })),
      subtotal: totals.subtotal,
      totalCgst: totals.totalCgst,
      totalSgst: totals.totalSgst,
      totalIgst: totals.totalIgst,
      totalTds: totals.totalTds,
      roundOff: totals.roundOff,
      subTotal: totals.subTotal,
      netTotal: totals.netAmount,
      // GL codes
      sgstGlCode: values.sgstGlCode,
      cgstGlCode: values.cgstGlCode,
      igstGlCode: values.igstGlCode,
      tdsGlCode: values.tdsGlCode,
      roundOffGlCode: values.roundOffGlCode,
      rcmSgstCode: values.rcmSgstCode,
      rcmCgstCode: values.rcmCgstCode,
      rcmIgstCode: values.rcmIgstCode,
      rcmCgstReceivableCode: values.rcmCgstReceivableCode,
      rcmSgstReceivableCode: values.rcmSgstReceivableCode,
      rcmIgstReceivableCode: values.rcmIgstReceivableCode,
      fieldPostingDate: vendorBillingSettingsData?.field_posting_date || 'bill_date',
    };
  };

  const getBlankBillValues = () => ({
    ...initialValues,
    tariffDetails: [{ ...emptyTariffRow }],
    sgstGlCode: vendorBillingSettingsData?.sgst_gl || '',
    cgstGlCode: vendorBillingSettingsData?.cgst_gl || '',
    igstGlCode: vendorBillingSettingsData?.igst_gl || '',
    tdsGlCode: vendorBillingSettingsData?.tds_gl || '',
    roundOffGlCode: vendorBillingSettingsData?.roundoff_gl || vendorBillingSettingsData?.round_off_gl || '',
    rcmSgstCode: vendorBillingSettingsData?.rcm_sgst_payable || '',
    rcmCgstCode: vendorBillingSettingsData?.rcm_cgst_payable || '',
    rcmIgstCode: vendorBillingSettingsData?.rcm_igst_payable || '',
    rcmCgstReceivableCode: vendorBillingSettingsData?.rcm_cgst_receivable || '',
    rcmSgstReceivableCode: vendorBillingSettingsData?.rcm_sgst_receivable || '',
    rcmIgstReceivableCode: vendorBillingSettingsData?.rcm_igst_receivable || '',
  });

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    const mode = submitModeRef.current;
    submitModeRef.current = 'post';
    const isDraft = mode === 'draft' || mode === 'queue-draft';
    const payload = buildBillPayload(values, isDraft);

    if (mode === 'queue-draft' || mode === 'queue-post') {
      const vendor = vendorsList.find(v => String(v.id) === String(values.vendorId));
      setQueuedBills(prev => [...prev, {
        id: nextQueuedBillId.current++,
        vendorName: vendor?.name || 'Vendor',
        billNumber: values.billNumber,
        isDraft,
        payload,
      }]);
      resetForm({ values: getBlankBillValues() });
      setSubmitting(false);
      return;
    }

    dispatch(createVendorBill({
      ...payload,
      onSuccess: () => {
        setSubmitting(false);
        navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS);
      }
    }));
  };

  const handleSubmitBatch = () => {
    if (queuedBills.length === 0 || createVendorBillsBatchLoading) return;
    setBatchResult(null);
    dispatch(createVendorBillsBatch({
      bills: queuedBills.map(({ id, vendorName, billNumber, isDraft, payload }) => ({
        id, vendorName, billNumber, isDraft, payload
      })),
      onSuccess: (result) => {
        setBatchResult(result);
        const failedIds = new Set((result.failed || []).map(bill => bill.id));
        setQueuedBills(current => current.filter(bill => failedIds.has(bill.id)));
      },
      onError: (error) => setBatchResult({
        total: queuedBills.length,
        created: [],
        failed: [{ vendor: 'Batch', bill_number: '', error: error?.message || 'Unable to create vendor bills' }]
      })
    }));
  };

  const handleCloseBatchResult = () => {
    const allCreated = !batchResult?.failed?.length;
    setBatchResult(null);
    dispatch(resetVendorBillsBatch());
    if (allCreated) {
      navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS);
    }
  };

  // Auto-set GL codes from billing settings
  useEffect(() => {
    if (vendorBillingSettingsData && setFieldValueRef.current) {
      setFieldValueRef.current('sgstGlCode', vendorBillingSettingsData.sgst_gl || '');
      setFieldValueRef.current('cgstGlCode', vendorBillingSettingsData.cgst_gl || '');
      setFieldValueRef.current('igstGlCode', vendorBillingSettingsData.igst_gl || '');
      setFieldValueRef.current('tdsGlCode', vendorBillingSettingsData.tds_gl || '');
      setFieldValueRef.current('roundOffGlCode', vendorBillingSettingsData.roundoff_gl || vendorBillingSettingsData.round_off_gl || '');
      setFieldValueRef.current('rcmSgstCode', vendorBillingSettingsData.rcm_sgst_payable || '');
      setFieldValueRef.current('rcmCgstCode', vendorBillingSettingsData.rcm_cgst_payable || '');
      setFieldValueRef.current('rcmIgstCode', vendorBillingSettingsData.rcm_igst_payable || '');
      setFieldValueRef.current('rcmCgstReceivableCode', vendorBillingSettingsData.rcm_cgst_receivable || '');
      setFieldValueRef.current('rcmSgstReceivableCode', vendorBillingSettingsData.rcm_sgst_receivable || '');
      setFieldValueRef.current('rcmIgstReceivableCode', vendorBillingSettingsData.rcm_igst_receivable || '');
    }
  }, [vendorBillingSettingsData]);

  const inputClass = "tw-w-full tw-px-3 tw-py-2.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500";
  const inputClassSmall = "tw-w-full tw-px-2 tw-py-2 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500";
  const disabledInputSmall = "tw-w-full tw-px-2 tw-py-2 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-gray-100 tw-cursor-not-allowed focus:tw-outline-none";
  const labelClass = "tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1 tw-block";

  return (
    <React.Fragment>
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <div className="tw-flex tw-items-center tw-gap-3">
          <button
            onClick={() => navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS)}
            className="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-600"
          >
            <Icon icon="mdi:arrow-left" className="tw-text-lg" />
          </button>
          <div>
            <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Add New Bill</h1>
            <p className="tw-text-xs tw-text-gray-500">Create one bill or prepare several bills for different vendors</p>
          </div>
        </div>
      </div>

      {queuedBills.length > 0 && (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mb-4 tw-p-5">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-3">
            <div>
              <h2 className="tw-font-semibold tw-text-gray-800">Bills ready to create ({queuedBills.length})</h2>
              <p className="tw-text-xs tw-text-gray-500">Each bill keeps its vendor, tax, tariff, and posting details.</p>
            </div>
            <button
              type="button"
              onClick={handleSubmitBatch}
              disabled={createVendorBillsBatchLoading}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-lg hover:tw-bg-blue-700 disabled:tw-bg-blue-400 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
            >
              {createVendorBillsBatchLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
              Create {queuedBills.length} {queuedBills.length === 1 ? 'Bill' : 'Bills'}
            </button>
          </div>
          {createVendorBillsBatchError && (
            <p role="alert" className="tw-mb-3 tw-rounded-md tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-700">
              {createVendorBillsBatchError}
            </p>
          )}
          <div className="tw-divide-y tw-divide-gray-100">
            {queuedBills.map((bill) => (
              <div key={bill.id} className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-py-3">
                <div>
                  <div className="tw-text-sm tw-font-medium tw-text-gray-800">{bill.vendorName} — {bill.billNumber}</div>
                  <div className="tw-text-xs tw-text-gray-500">
                    {bill.isDraft ? 'Draft' : 'Posted'} · {formatCurrency(bill.payload.netTotal)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setQueuedBills(current => current.filter(item => item.id !== bill.id))}
                  disabled={createVendorBillsBatchLoading}
                  className="tw-text-sm tw-text-red-600 hover:tw-text-red-700 disabled:tw-opacity-50"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100">
        <Formik
          initialValues={initialValues}
          validationSchema={addVendorBillValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, setFieldValue, submitForm }) => {
            setFieldValueRef.current = setFieldValue;
            const grandTotals = calcGrandTotals(values.tariffDetails, values.rcmApplicable);

            return (
              <Form>
                <div className="tw-p-6 tw-space-y-5">
                  {/* Row 1: Vendor & Type */}
                  <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                    <div>
                      <label className={labelClass}>
                        Vendor <span className="tw-text-red-500">*</span>
                      </label>
                      <div className="tw-relative">
                        <Field
                          as="select"
                          name="vendorId"
                          disabled={allVendorsLoading}
                          className={`${inputClass} tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed`}
                          onChange={(e) => {
                            const vendorId = e.target.value;
                            setFieldValue('vendorId', vendorId);
                            if (vendorId) {
                              dispatch(getAllVendorTariffs({ vendorId }));
                              const selectedVendor = vendorsList.find(v => String(v.id) === vendorId);
                              if (selectedVendor) {
                                setFieldValue('rcmApplicable', selectedVendor.rcmApplicable || 'N');
                                setFieldValue('tdsApplicable', selectedVendor.tdsApplicable || 'N');
                                setFieldValue('panNumber', selectedVendor.panNumber || '');
                              }
                            }
                            setFieldValue('tariffDetails', [{ ...emptyTariffRow }]);
                          }}
                        >
                          <option value="">{allVendorsLoading ? 'Loading...' : 'Select vendor'}</option>
                          {vendorsList.map(v => (
                            <option key={v.id} value={v.id}>{v.name}</option>
                          ))}
                        </Field>
                        {vendorSettingLoading && (
                          <Icon icon="mdi:loading" className="tw-absolute tw-right-8 tw-top-1/2 tw--translate-y-1/2 tw-text-blue-500 tw-animate-spin tw-text-sm" />
                        )}
                      </div>
                      <ErrorMessage name="vendorId" component="p" className="tw-text-xs tw-text-red-500 tw-mt-1" />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Type <span className="tw-text-red-500">*</span>
                      </label>
                      <Field
                        as="select"
                        name="billType"
                        className={`${inputClass} tw-bg-white`}
                      >
                        <option value="">Select type</option>
                        {BILL_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="billType" component="p" className="tw-text-xs tw-text-red-500 tw-mt-1" />
                    </div>
                  </div>

                  {/* Row 2: Bill Number, Bill Date, Due Date */}
                  <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                    <div>
                      <label className={labelClass}>
                        Bill Number <span className="tw-text-red-500">*</span>
                      </label>
                      <Field
                        type="text"
                        name="billNumber"
                        placeholder="VB-2024-XXX"
                        className={inputClass}
                      />
                      <ErrorMessage name="billNumber" component="p" className="tw-text-xs tw-text-red-500 tw-mt-1" />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Bill Date <span className="tw-text-red-500">*</span>
                      </label>
                      <Field
                        type="date"
                        name="billDate"
                        className={inputClass}
                      />
                      <ErrorMessage name="billDate" component="p" className="tw-text-xs tw-text-red-500 tw-mt-1" />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Due Date <span className="tw-text-red-500">*</span>
                      </label>
                      <Field
                        type="date"
                        name="dueDate"
                        className={inputClass}
                      />
                      <ErrorMessage name="dueDate" component="p" className="tw-text-xs tw-text-red-500 tw-mt-1" />
                    </div>
                  </div>

                  {/* Row 3: Description */}
                  <div>
                    <label className={labelClass}>
                      Description <span className="tw-text-red-500">*</span>
                    </label>
                    <JoditEditor
                      ref={descriptionEditorRef}
                      value={values.description || ''}
                      config={editorConfig}
                      onBlur={(newContent) => setFieldValue('description', newContent)}
                    />
                    <ErrorMessage name="description" component="p" className="tw-text-xs tw-text-red-500 tw-mt-1" />
                  </div>

                  {/* RCM & TDS Indicators */}
                  {values.vendorId && (
                    <div className="tw-flex tw-gap-4">
                      {values.rcmApplicable === 'Y' && (
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg">
                          <Icon icon="mdi:alert-circle-outline" className="tw-text-amber-500 tw-text-sm" />
                          <span className="tw-text-xs tw-font-medium tw-text-amber-700">RCM Applicable</span>
                        </div>
                      )}
                      {values.tdsApplicable === 'Y' && (
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-purple-50 tw-border tw-border-purple-200 tw-rounded-lg">
                          <Icon icon="mdi:percent" className="tw-text-purple-500 tw-text-sm" />
                          <span className="tw-text-xs tw-font-medium tw-text-purple-700">TDS Applicable</span>
                        </div>
                      )}
                      {!values.panNumber && (
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg">
                          <Icon icon="mdi:alert" className="tw-text-red-500 tw-text-sm" />
                          <span className="tw-text-xs tw-font-medium tw-text-red-700">No PAN - TDS at 20%</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tariff Details Section */}
                  <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                    <div className="tw-bg-blue-50 tw-px-4 tw-py-3">
                      <h3 className="tw-text-sm tw-font-semibold tw-text-blue-800">Tariff Details</h3>
                    </div>

                    <FieldArray name="tariffDetails">
                      {({ push, remove }) => (
                        <div>
                          <div className="tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-sm" style={{ minWidth: '950px' }}>
                              <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                  <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '40px' }}>#</th>
                                  <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '180px' }}>Head Name</th>
                                  {itemType === 'unit' ? (
                                    <>
                                      <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '70px' }}>Qty</th>
                                      <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '90px' }}>Unit Rate</th>
                                      <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '100px' }}>Amount</th>
                                    </>
                                  ) : (
                                    <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '100px' }}>Amount</th>
                                  )}
                                  <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '70px' }}>CGST %</th>
                                  <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '70px' }}>SGST %</th>
                                  <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '70px' }}>IGST %</th>
                                  <th className="tw-text-left tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '70px' }}>TDS %</th>
                                  <th className="tw-text-right tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase" style={{ width: '100px' }}>Total</th>
                                  <th className="tw-px-2 tw-py-2.5" style={{ width: '36px' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {values.tariffDetails.map((row, index) => {
                                  const rowTotals = calcRowTotals(row);
                                  const isHeadSelected = !!row.headId;

                                  return (
                                    <tr key={index} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                      {/* # */}
                                      <td className="tw-px-3 tw-py-2.5 tw-text-gray-400 tw-align-top tw-pt-3 tw-text-xs">
                                        {index + 1}
                                      </td>
                                      {/* Head Name */}
                                      <td className="tw-px-3 tw-py-2 tw-align-top">
                                        <Field
                                          as="select"
                                          name={`tariffDetails.${index}.headId`}
                                          disabled={allVendorTariffsLoading || !values.vendorId}
                                          className={`${inputClassSmall} tw-bg-white disabled:tw-bg-gray-100`}
                                          onChange={(e) => {
                                            const selectedId = e.target.value;
                                            const selectedTariff = vendorTariffsList.find(t => String(t.id) === selectedId);
                                            setFieldValue(`tariffDetails.${index}.headId`, selectedId);
                                            setFieldValue(`tariffDetails.${index}.headName`, selectedTariff?.itemName || '');
                                            setFieldValue(`tariffDetails.${index}.glCode`, selectedTariff?.glCode || '');
                                            setFieldValue(`tariffDetails.${index}.itemType`, selectedTariff?.itemType || '');
                                            if (selectedTariff) {
                                              setFieldValue(`tariffDetails.${index}.amount`, selectedTariff.unitRate);
                                              if (itemType === 'unit') {
                                                setFieldValue(`tariffDetails.${index}.qty`, 1);
                                                setFieldValue(`tariffDetails.${index}.unit`, selectedTariff.unit || 'nos');
                                              }
                                              setFieldValue(`tariffDetails.${index}.cgstRate`, selectedTariff.cgstRate);
                                              setFieldValue(`tariffDetails.${index}.sgstRate`, selectedTariff.sgstRate);
                                              setFieldValue(`tariffDetails.${index}.igstRate`, selectedTariff.igstRate);
                                              setFieldValue(`tariffDetails.${index}.tdsSection`, selectedTariff.tdsSection);
                                              // Set TDS rate: 20% if no PAN, else from tariff, 0 if TDS not applicable
                                              let tdsRate = selectedTariff.tdsRate;
                                              if (!values.panNumber || values.panNumber.trim() === '') {
                                                tdsRate = 20;
                                              }
                                              if (values.tdsApplicable === 'N') {
                                                tdsRate = 0;
                                              }
                                              setFieldValue(`tariffDetails.${index}.tdsRate`, tdsRate);
                                            }
                                          }}
                                        >
                                          <option value="">{allVendorTariffsLoading ? 'Loading...' : !values.vendorId ? 'Select vendor first' : 'Select tariff'}</option>
                                          {vendorTariffsList.map(t => (
                                            <option key={t.id} value={t.id}>{t.itemName}</option>
                                          ))}
                                        </Field>
                                        <ErrorMessage name={`tariffDetails.${index}.headId`} component="p" className="tw-text-[10px] tw-text-red-500 tw-mt-0.5" />
                                      </td>
                                      {/* Qty, Unit Rate & Amount (for unit type) */}
                                      {itemType === 'unit' ? (
                                        <>
                                          <td className="tw-px-3 tw-py-2 tw-align-top">
                                            <Field
                                              type="number"
                                              name={`tariffDetails.${index}.qty`}
                                              placeholder="0"
                                              min="0"
                                              className={inputClassSmall}
                                            />
                                            <ErrorMessage name={`tariffDetails.${index}.qty`} component="p" className="tw-text-[10px] tw-text-red-500 tw-mt-0.5" />
                                          </td>
                                          <td className="tw-px-3 tw-py-2 tw-align-top">
                                            <Field
                                              type="number"
                                              name={`tariffDetails.${index}.amount`}
                                              placeholder="0.00"
                                              min="0"
                                              className={inputClassSmall}
                                            />
                                            <ErrorMessage name={`tariffDetails.${index}.amount`} component="p" className="tw-text-[10px] tw-text-red-500 tw-mt-0.5" />
                                          </td>
                                          <td className="tw-px-3 tw-py-2 tw-align-top">
                                            <input
                                              type="text"
                                              disabled
                                              value={formatCurrency(safeParseFloat(row.qty) * safeParseFloat(row.amount))}
                                              className={disabledInputSmall}
                                            />
                                          </td>
                                        </>
                                      ) : (
                                        /* Amount (for amount type) */
                                        <td className="tw-px-3 tw-py-2 tw-align-top">
                                          <Field
                                            type="number"
                                            name={`tariffDetails.${index}.amount`}
                                            placeholder="0.00"
                                            min="0"
                                            className={inputClassSmall}
                                          />
                                          <ErrorMessage name={`tariffDetails.${index}.amount`} component="p" className="tw-text-[10px] tw-text-red-500 tw-mt-0.5" />
                                        </td>
                                      )}
                                      {/* CGST % */}
                                      <td className="tw-px-3 tw-py-2 tw-align-top">
                                        {isHeadSelected ? (
                                          <>
                                            <Field
                                              type="number"
                                              name={`tariffDetails.${index}.cgstRate`}
                                              placeholder="0"
                                              min="0"
                                              className={inputClassSmall}
                                            />
                                            <ErrorMessage name={`tariffDetails.${index}.cgstRate`} component="p" className="tw-text-[10px] tw-text-red-500 tw-mt-0.5" />
                                          </>
                                        ) : (
                                          <input type="text" disabled placeholder="-" className={disabledInputSmall} />
                                        )}
                                      </td>
                                      {/* SGST % */}
                                      <td className="tw-px-3 tw-py-2 tw-align-top">
                                        {isHeadSelected ? (
                                          <>
                                            <Field
                                              type="number"
                                              name={`tariffDetails.${index}.sgstRate`}
                                              placeholder="0"
                                              min="0"
                                              className={inputClassSmall}
                                            />
                                            <ErrorMessage name={`tariffDetails.${index}.sgstRate`} component="p" className="tw-text-[10px] tw-text-red-500 tw-mt-0.5" />
                                          </>
                                        ) : (
                                          <input type="text" disabled placeholder="-" className={disabledInputSmall} />
                                        )}
                                      </td>
                                      {/* IGST % */}
                                      <td className="tw-px-3 tw-py-2 tw-align-top">
                                        {isHeadSelected ? (
                                          <>
                                            <Field
                                              type="number"
                                              name={`tariffDetails.${index}.igstRate`}
                                              placeholder="0"
                                              min="0"
                                              className={inputClassSmall}
                                            />
                                            <ErrorMessage name={`tariffDetails.${index}.igstRate`} component="p" className="tw-text-[10px] tw-text-red-500 tw-mt-0.5" />
                                          </>
                                        ) : (
                                          <input type="text" disabled placeholder="-" className={disabledInputSmall} />
                                        )}
                                      </td>
                                      {/* TDS % */}
                                      <td className="tw-px-3 tw-py-2 tw-align-top">
                                        {isHeadSelected ? (
                                          <div>
                                            <Field
                                              type="number"
                                              name={`tariffDetails.${index}.tdsRate`}
                                              placeholder="0"
                                              min="0"
                                              className={`${disabledInputSmall}`}
                                              disabled
                                            />
                                            {row.tdsSection && (
                                              <p className="tw-text-[10px] tw-text-gray-500 tw-mt-0.5 tw-whitespace-nowrap">
                                                Sec: {row.tdsSection}
                                              </p>
                                            )}
                                          </div>
                                        ) : (
                                          <input type="text" disabled placeholder="-" className={disabledInputSmall} />
                                        )}
                                      </td>
                                      {/* Total */}
                                      <td className="tw-px-3 tw-py-2 tw-text-right tw-align-top tw-pt-3">
                                        <span className="tw-font-semibold tw-text-gray-800 tw-text-xs tw-whitespace-nowrap">
                                          {formatCurrency(rowTotals.rowTotal)}
                                        </span>
                                      </td>
                                      {/* Remove */}
                                      <td className="tw-px-2 tw-py-2 tw-text-center tw-align-top tw-pt-3">
                                        {values.tariffDetails.length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() => remove(index)}
                                            className="tw-p-1 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500"
                                            title="Remove row"
                                          >
                                            <Icon icon="mdi:close" className="tw-text-base" />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Add More Button */}
                          <div className="tw-px-4 tw-py-3 tw-border-t tw-border-gray-100">
                            <button
                              type="button"
                              onClick={() => push({ ...emptyTariffRow })}
                              className="tw-flex tw-items-center tw-gap-1.5 tw-text-blue-600 hover:tw-text-blue-700 tw-text-sm tw-font-medium"
                            >
                              <Icon icon="mdi:plus-circle-outline" className="tw-text-base" />
                              Add More
                            </button>
                          </div>
                        </div>
                      )}
                    </FieldArray>

                    {/* Totals / Summary Section */}
                    <div className="tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-px-4 tw-py-4">
                      <div className="tw-flex tw-justify-end">
                        <div className="tw-w-80 tw-space-y-1.5">
                          {/* Subtotal (base amount) */}
                          <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                            <span className="tw-text-gray-600">Subtotal</span>
                            <span className="tw-font-medium tw-text-gray-800">{formatCurrency(grandTotals.subtotal)}</span>
                          </div>

                          {/* Normal GST (when RCM is N) */}
                          {values.rcmApplicable !== 'Y' && (
                            <>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600">SGST</span>
                                <span className="tw-font-medium tw-text-gray-800">{formatCurrency(grandTotals.totalSgst)}</span>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600">CGST</span>
                                <span className="tw-font-medium tw-text-gray-800">{formatCurrency(grandTotals.totalCgst)}</span>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600">IGST</span>
                                <span className="tw-font-medium tw-text-gray-800">{formatCurrency(grandTotals.totalIgst)}</span>
                              </div>
                            </>
                          )}

                          {/* RCM GST (when RCM is Y) */}
                          {values.rcmApplicable === 'Y' && (
                            <>
                              <div className="tw-border-t tw-border-gray-200 tw-pt-1.5 tw-mt-1">
                                <p className="tw-text-xs tw-font-semibold tw-text-amber-700 tw-mb-1">RCM Payable</p>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600 tw-pl-2">SGST (Payable)</span>
                                <span className="tw-font-medium tw-text-gray-800">{formatCurrency(grandTotals.rcmSgst)}</span>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600 tw-pl-2">CGST (Payable)</span>
                                <span className="tw-font-medium tw-text-gray-800">{formatCurrency(grandTotals.rcmCgst)}</span>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600 tw-pl-2">IGST (Payable)</span>
                                <span className="tw-font-medium tw-text-gray-800">{formatCurrency(grandTotals.rcmIgst)}</span>
                              </div>
                              <div className="tw-border-t tw-border-gray-200 tw-pt-1.5 tw-mt-1">
                                <p className="tw-text-xs tw-font-semibold tw-text-green-700 tw-mb-1">RCM Receivable</p>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600 tw-pl-2">SGST (Receivable)</span>
                                <span className="tw-font-medium tw-text-red-600">{formatCurrency(-1 * grandTotals.rcmSgst)}</span>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600 tw-pl-2">CGST (Receivable)</span>
                                <span className="tw-font-medium tw-text-red-600">{formatCurrency(-1 * grandTotals.rcmCgst)}</span>
                              </div>
                              <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                                <span className="tw-text-gray-600 tw-pl-2">IGST (Receivable)</span>
                                <span className="tw-font-medium tw-text-red-600">{formatCurrency(-1 * grandTotals.rcmIgst)}</span>
                              </div>
                            </>
                          )}

                          {/* Round Off */}
                          <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                            <span className="tw-text-gray-600">Round Off</span>
                            <span className="tw-font-medium tw-text-gray-800">
                              {grandTotals.roundOff >= 0 ? '+' : ''}{grandTotals.roundOff.toFixed(2)}
                            </span>
                          </div>

                          {/* Sub Total (after round off) */}
                          <div className="tw-border-t tw-border-gray-300 tw-pt-2 tw-mt-1 tw-flex tw-justify-between">
                            <span className="tw-font-semibold tw-text-gray-800 tw-text-sm">Sub Total</span>
                            <span className="tw-font-bold tw-text-gray-800">{formatCurrency(grandTotals.subTotal)}</span>
                          </div>

                          {/* TDS */}
                          {grandTotals.totalTds > 0 && (
                            <div className="tw-flex tw-justify-between tw-text-sm tw-py-0.5">
                              <span className="tw-text-gray-600">TDS Deduction</span>
                              <span className="tw-font-medium tw-text-red-600">- {formatCurrency(grandTotals.totalTds)}</span>
                            </div>
                          )}

                          {/* Net Total */}
                          <div className="tw-border-t tw-border-gray-300 tw-pt-2 tw-mt-1 tw-flex tw-justify-between">
                            <span className="tw-font-semibold tw-text-gray-800 tw-text-sm">Net Total</span>
                            <span className="tw-font-bold tw-text-blue-700 tw-text-base">{formatCurrency(grandTotals.netAmount)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-gray-100 tw-bg-gray-50 tw-rounded-b-lg">
                  <button
                    type="button"
                    onClick={() => navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS)}
                    className="tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting || createVendorBillLoading || createVendorBillsBatchLoading}
                    onClick={() => { submitModeRef.current = 'queue-draft'; submitForm(); }}
                    className="tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-amber-300 tw-rounded-lg hover:tw-bg-amber-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
                  >
                    <Icon icon="mdi:playlist-plus" /> Add Draft to Batch
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting || createVendorBillLoading || createVendorBillsBatchLoading}
                    onClick={() => { submitModeRef.current = 'queue-post'; submitForm(); }}
                    className="tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-blue-700 tw-bg-blue-50 tw-border tw-border-blue-300 tw-rounded-lg hover:tw-bg-blue-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
                  >
                    <Icon icon="mdi:playlist-plus" /> Add Posted Bill to Batch
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || createVendorBillLoading || createVendorBillsBatchLoading}
                    onClick={() => { submitModeRef.current = 'draft'; }}
                    className="tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-amber-300 tw-rounded-lg hover:tw-bg-amber-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
                  >
                    {isSubmitting || createVendorBillLoading ? <Icon icon="mdi:loading" className="tw-animate-spin" /> : null}
                    <Icon icon="mdi:content-save-outline" /> Save as Draft
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || createVendorBillLoading || createVendorBillsBatchLoading}
                    onClick={() => { submitModeRef.current = 'post'; }}
                    className="tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-lg hover:tw-bg-blue-700 disabled:tw-bg-blue-400 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
                  >
                    {isSubmitting || createVendorBillLoading ? <Icon icon="mdi:loading" className="tw-animate-spin" /> : null}
                    <Icon icon="mdi:check-circle-outline" /> Save
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>

      {batchResult && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50 tw-p-4">
          <div className="tw-w-full tw-max-w-lg tw-rounded-lg tw-bg-white tw-p-6 tw-shadow-xl">
            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-800">Vendor bill batch results</h2>
            <p className="tw-mt-2 tw-text-sm tw-text-gray-600">
              Created {batchResult.created?.length || 0} of {batchResult.total || 0} bills.
            </p>
            {batchResult.created?.length > 0 && (
              <div className="tw-mt-3 tw-text-sm tw-text-green-700">
                {batchResult.created.map(bill => (
                  <div key={bill.id}>{bill.vendor} — {bill.bill_number} ({bill.status})</div>
                ))}
              </div>
            )}
            {batchResult.failed?.length > 0 && (
              <div className="tw-mt-4">
                <h3 className="tw-text-sm tw-font-semibold tw-text-red-700">Bills not created</h3>
                <div className="tw-mt-1 tw-max-h-48 tw-space-y-2 tw-overflow-y-auto tw-text-sm tw-text-red-700">
                  {batchResult.failed.map((bill, index) => (
                    <div key={bill.id || `${bill.bill_number}-${index}`}>
                      <strong>{bill.vendor} — {bill.bill_number}:</strong> {bill.error}
                    </div>
                  ))}
                </div>
                <p className="tw-mt-3 tw-text-xs tw-text-gray-500">
                  Failed bills remain in the batch. Remove them and add corrected entries before retrying.
                </p>
              </div>
            )}
            <div className="tw-mt-5 tw-flex tw-justify-end">
              <button
                type="button"
                onClick={handleCloseBatchResult}
                className="tw-rounded-lg tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700"
              >
                {batchResult.failed?.length ? 'Close' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default AddVendorBill;
